import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  User, Mission, Chat, Message, Lang, Theme,
  PLATFORM_FEE_PERCENT, PAYMENT_ON_HIRE, PAYMENT_ON_START, PAYMENT_ON_FINISH,
} from '../types';
import { db } from '../lib/db';
import {
  sanitize, clamp, isValidEmail, isValidPassword,
  weakHash, generateId, checkRateLimit,
} from '../lib/security';

// ─── Auth slice ───────────────────────────────────────────────
interface AuthSlice {
  currentUser: User | null;
  login: (email: string, password: string) => string | null; // returns error key or null
  register: (d: {
    firstName: string; lastName: string; email: string;
    password: string; country: string; city: string;
  }) => string | null;
  logout: () => void;
  updateCurrentUser: (data: Partial<User>) => void;
}

// ─── App slice ────────────────────────────────────────────────
interface AppSlice {
  theme: Theme;
  lang: Lang;
  toggleTheme: () => void;
  setLang: (l: Lang) => void;
  mobileNavOpen: boolean;
  setMobileNavOpen: (v: boolean) => void;
}

// ─── Mission slice ────────────────────────────────────────────
interface MissionSlice {
  missions: Mission[];
  refreshMissions: () => void;
  createMission: (d: {
    title: string; description: string; reward: number;
    estimatedHours: number; category: string; location: string; skills: string[];
  }) => string | null;
  applyMission: (missionId: string) => { error: string | null; chatId: string | null };
  hireMercenary: (chatId: string) => string | null;
  startMission: (missionId: string) => string | null;
  completeMission: (missionId: string) => string | null;
}

// ─── Chat slice ───────────────────────────────────────────────
interface ChatSlice {
  activeChatId: string | null;
  setActiveChatId: (id: string | null) => void;
  sendMessage: (chatId: string, text: string) => void;
  startDirectChat: (otherUserId: string) => string;
  getMyChats: () => Chat[];
  unreadCount: () => number;
}

// ─── Report slice ─────────────────────────────────────────────
interface ReportSlice {
  reportUser: (targetId: string, reason: string, details: string) => string | null;
}

type Store = AuthSlice & AppSlice & MissionSlice & ChatSlice & ReportSlice;

export const useStore = create<Store>()(
  persist(
    (set, get) => ({
      // ── App ──────────────────────────────────────────────────
      theme: 'light',
      lang: 'en',
      mobileNavOpen: false,
      toggleTheme: () => set((s) => ({ theme: s.theme === 'light' ? 'dark' : 'light' })),
      setLang: (lang) => set({ lang }),
      setMobileNavOpen: (v) => set({ mobileNavOpen: v }),

      // ── Auth ─────────────────────────────────────────────────
      currentUser: null,

      login: (email, password) => {
        if (!checkRateLimit('login', 10, 60_000)) return 'err_rate_limit';
        const user = db.getUserByEmail(email.trim().toLowerCase());
        if (!user) return 'err_invalid_creds';
        if (user.blocked) return 'err_blocked';
        // In production: server compares bcrypt hash
        if ((user as any).password !== password && weakHash(password) !== (user as any).passwordHash)
          return 'err_invalid_creds';
        set({ currentUser: user });
        get().refreshMissions();
        return null;
      },

      register: ({ firstName, lastName, email, password, country, city }) => {
        if (!checkRateLimit('register', 5, 60_000)) return 'err_rate_limit';
        if (!firstName.trim() || !lastName.trim() || !email.trim() || !password || !city.trim())
          return 'err_fill_fields';
        if (!isValidEmail(email)) return 'err_email_invalid';
        if (!isValidPassword(password)) return 'err_password_short';
        if (db.getUserByEmail(email)) return 'err_email_taken';

        const user = db.createUser({
          firstName: sanitize(clamp(firstName.trim(), 50)),
          lastName: sanitize(clamp(lastName.trim(), 50)),
          email: email.trim().toLowerCase(),
          password, // server must hash; adapter stores plaintext only in demo
          country,
          city: sanitize(clamp(city.trim(), 80)),
          bio: '',
          avatar: null,
          skills: [],
          rating: 0,
          ratingCount: 0,
          reports: [],
          blocked: false,
          role: 'user',
        } as any);

        set({ currentUser: user });
        get().refreshMissions();
        return null;
      },

      logout: () => set({ currentUser: null, activeChatId: null }),

      updateCurrentUser: (data) => {
        const { currentUser } = get();
        if (!currentUser) return;
        const updated = db.updateUser(currentUser.id, data);
        if (updated) set({ currentUser: updated });
      },

      // ── Missions ─────────────────────────────────────────────
      missions: db.getMissions(),

      refreshMissions: () => set({ missions: db.getMissions() }),

      createMission: (d) => {
        const { currentUser } = get();
        if (!currentUser) return 'err_not_auth';
        if (!d.title.trim() || !d.description.trim()) return 'err_fill_fields';
        if (d.reward < 5) return 'err_min_reward';
        if (d.estimatedHours < 0.5) return 'err_fill_fields';

        db.createMission({
          title: sanitize(clamp(d.title.trim(), 120)),
          description: sanitize(clamp(d.description.trim(), 2000)),
          reward: Math.round(d.reward * 100) / 100,
          estimatedHours: d.estimatedHours,
          category: d.category,
          location: sanitize(clamp(d.location.trim(), 120)) || 'Remote',
          skills: d.skills.map((s) => sanitize(clamp(s, 40))).slice(0, 10),
          posterId: currentUser.id,
          status: 'open',
          applicantIds: [],
          hiredId: null,
          payments: { hired: 0, started: 0, finished: 0 },
          stripePaymentIntentId: null,
        });
        get().refreshMissions();
        return null;
      },

      applyMission: (missionId) => {
        const { currentUser } = get();
        if (!currentUser) return { error: 'err_not_auth', chatId: null };
        const mission = db.getMissionById(missionId);
        if (!mission) return { error: 'err_not_found', chatId: null };
        if (mission.posterId === currentUser.id) return { error: 'err_own_mission', chatId: null };
        if (mission.applicantIds.includes(currentUser.id)) return { error: 'err_already_applied', chatId: null };

        db.updateMission(missionId, { applicantIds: [...mission.applicantIds, currentUser.id] });

        // Create or find chat
        const chatId = `mission_${missionId}_${currentUser.id}`;
        if (!db.getChatById(chatId)) {
          db.createChat({
            id: chatId,
            missionId,
            participantIds: [mission.posterId, currentUser.id],
            hireStatus: 'pending',
            missionStatus: 'open',
            messages: [{
              id: generateId(), chatId, senderId: 'system',
              text: `📋 ${sanitize(currentUser.firstName)} applied for: "${sanitize(mission.title)}"`,
              ts: Date.now(), read: false,
            }],
            isDirect: false,
            lastMessageAt: Date.now(),
          });
        }
        get().refreshMissions();
        return { error: null, chatId };
      },

      hireMercenary: (chatId) => {
        const { currentUser } = get();
        if (!currentUser) return 'err_not_auth';
        const chat = db.getChatById(chatId);
        if (!chat || !chat.missionId) return 'err_not_found';
        const mission = db.getMissionById(chat.missionId);
        if (!mission || mission.posterId !== currentUser.id) return 'err_forbidden';

        const hiredId = chat.participantIds.find((id) => id !== currentUser.id)!;
        const hiredUser = db.getUserById(hiredId);
        const pct10 = Math.round(mission.reward * PAYMENT_ON_HIRE * 100) / 100;

        db.updateMission(mission.id, {
          hiredId,
          status: 'active',
          payments: { ...mission.payments, hired: pct10 },
        });
        db.updateChat(chatId, { hireStatus: 'hired', missionStatus: 'active' });
        db.addMessage(chatId, {
          chatId, senderId: 'system',
          text: `🤠 HIRED! ${sanitize(hiredUser?.firstName ?? 'Mercenary')} is on the job. €${pct10.toFixed(2)} (10%) sent.`,
          ts: Date.now(), read: false,
        });
        get().refreshMissions();
        return null;
      },

      startMission: (missionId) => {
        const { currentUser } = get();
        if (!currentUser) return 'err_not_auth';
        const mission = db.getMissionById(missionId);
        if (!mission || mission.posterId !== currentUser.id) return 'err_forbidden';

        const pct40 = Math.round(mission.reward * PAYMENT_ON_START * 100) / 100;
        db.updateMission(missionId, {
          status: 'started',
          payments: { ...mission.payments, started: pct40 },
        });
        const chatId = Object.keys(db.getChats()).find(
          (k) => db.getChatById(k)?.missionId === missionId && db.getChatById(k)?.hireStatus === 'hired'
        );
        if (chatId) {
          db.updateChat(chatId, { missionStatus: 'started' });
          db.addMessage(chatId, {
            chatId, senderId: 'system',
            text: `▶ Mission STARTED. €${pct40.toFixed(2)} (40%) sent to mercenary.`,
            ts: Date.now(), read: false,
          });
        }
        get().refreshMissions();
        return null;
      },

      completeMission: (missionId) => {
        const { currentUser } = get();
        if (!currentUser) return 'err_not_auth';
        const mission = db.getMissionById(missionId);
        if (!mission || mission.posterId !== currentUser.id) return 'err_forbidden';

        const pct50 = Math.round(mission.reward * PAYMENT_ON_FINISH * 100) / 100;
        const platformCut = Math.round(mission.reward * PLATFORM_FEE_PERCENT * 100) / 100;
        db.updateMission(missionId, {
          status: 'done',
          payments: { ...mission.payments, finished: pct50 },
        });
        const chatId = Object.keys(db.getChats()).find(
          (k) => db.getChatById(k)?.missionId === missionId
        );
        if (chatId) {
          db.updateChat(chatId, { missionStatus: 'done' });
          db.addMessage(chatId, {
            chatId, senderId: 'system',
            text: `✅ Mission COMPLETE! Final €${pct50.toFixed(2)} (50%) sent. Total: €${mission.reward}. Platform earned: €${platformCut.toFixed(2)}.`,
            ts: Date.now(), read: false,
          });
        }
        get().refreshMissions();
        return null;
      },

      // ── Chat ─────────────────────────────────────────────────
      activeChatId: null,
      setActiveChatId: (id) => set({ activeChatId: id }),

      sendMessage: (chatId, text) => {
        const { currentUser } = get();
        if (!currentUser || !text.trim()) return;
        db.addMessage(chatId, {
          chatId,
          senderId: currentUser.id,
          text: sanitize(clamp(text.trim(), 2000)),
          ts: Date.now(),
          read: false,
        });
        set({}); // trigger re-render
      },

      startDirectChat: (otherUserId) => {
        const { currentUser } = get();
        if (!currentUser) return '';
        const chatId = `direct_${[currentUser.id, otherUserId].sort().join('_')}`;
        if (!db.getChatById(chatId)) {
          db.createChat({
            id: chatId,
            missionId: null,
            participantIds: [currentUser.id, otherUserId],
            hireStatus: 'n/a',
            missionStatus: 'n/a',
            messages: [],
            isDirect: true,
            lastMessageAt: Date.now(),
          });
        }
        return chatId;
      },

      getMyChats: () => {
        const { currentUser } = get();
        if (!currentUser) return [];
        return db.getChatsByUser(currentUser.id)
          .sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0));
      },

      unreadCount: () => {
        const { currentUser } = get();
        if (!currentUser) return 0;
        return db.getChatsByUser(currentUser.id).reduce((acc, chat) => {
          return acc + chat.messages.filter(
            (m) => !m.read && m.senderId !== currentUser.id && m.senderId !== 'system'
          ).length;
        }, 0);
      },

      // ── Reports ──────────────────────────────────────────────
      reportUser: (targetId, reason, details) => {
        const { currentUser } = get();
        if (!currentUser) return 'err_not_auth';
        if (targetId === currentUser.id) return 'err_self_report';
        const target = db.getUserById(targetId);
        if (!target) return 'err_not_found';
        if (target.reports.includes(currentUser.id)) return 'err_already_reported';

        const updatedReports = [...target.reports, currentUser.id];
        const shouldBlock = updatedReports.length >= 3;
        db.updateUser(targetId, { reports: updatedReports, blocked: shouldBlock });
        db.createReport({
          reporterId: currentUser.id, targetId,
          reason: sanitize(reason),
          details: sanitize(clamp(details, 1000)),
          ts: Date.now(),
        });
        return shouldBlock ? 'blocked' : null;
      },
    }),
    {
      name: 'mm-app-state',
      partialize: (s) => ({ theme: s.theme, lang: s.lang, currentUser: s.currentUser }),
    }
  )
);

// Helpers
export const getChats = () => db.getChats();
export const getChatById = (id: string) => db.getChatById(id);
export const getUsers = () => db.getUsers();
export const getUserById = (id: string) => db.getUserById(id);
